type CookieDeleter = (value: string) => void;

interface BrowserCookieCleanupOptions {
  cookieString: string;
  hostname: string;
  deleteCookie: CookieDeleter;
}

interface BrowserCookieCleanupResult {
  cookiesDeleted: boolean;
  shouldReload: boolean;
}

const EXTERNAL_ENVIRONMENT_PREFIXES: Record<string, string | null> = {
  'uhci-ui-external-dev.optum.com': 'DEV',
  'uhcinsights-demoui-hcc.uhc.com': null,
  'uhcinsights-stage.uhc.com': null,
  'uhcinsights.uhc.com': null,
};

const ESSENTIAL_UNPREFIXED_COOKIES = new Set([
  'ACCESS_TOKEN',
  'REFRESH_TOKEN',
  'SESSION_COUNT',
  'ID_TOKEN',
]);

function getInternalEnvironmentPrefix(hostname: string): string | null {
  if (hostname.includes('-dev')) return 'DEV';
  if (hostname.includes('-int.')) return 'INT';
  if (hostname.includes('-stg')) return 'STG';
  if (hostname.startsWith('localhost')) return 'LOCAL';
  return null;
}

function isPlatformCookie(name: string): boolean {
  return name.startsWith('dt') || name.startsWith('rx') || name.startsWith('session');
}

function isEssentialUnprefixedCookie(name: string): boolean {
  return ESSENTIAL_UNPREFIXED_COOKIES.has(name) || name.startsWith('session');
}

function getCookieNames(cookieString: string): string[] {
  return cookieString
    .split(';')
    .map((cookie) => cookie.split('=', 1)[0].trim())
    .filter(Boolean);
}

function deleteCookieForAllScopes(
  name: string,
  hostname: string,
  deleteCookie: CookieDeleter
): void {
  const expiration = '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/';
  deleteCookie(`${name}${expiration};`);
  deleteCookie(`${name}${expiration}; domain=${hostname};`);

  const domainParts = hostname.split('.');
  if (domainParts.length >= 2) {
    const parentDomain = `.${domainParts.slice(-2).join('.')}`;
    deleteCookie(`${name}${expiration}; domain=${parentDomain};`);
  }
}

export function cleanupBrowserCookies({
  cookieString,
  hostname,
  deleteCookie,
}: BrowserCookieCleanupOptions): BrowserCookieCleanupResult {
  const externalPrefix = EXTERNAL_ENVIRONMENT_PREFIXES[hostname];
  const isExternalEnvironment = Object.prototype.hasOwnProperty.call(
    EXTERNAL_ENVIRONMENT_PREFIXES,
    hostname
  );
  const environmentPrefix = isExternalEnvironment
    ? externalPrefix
    : getInternalEnvironmentPrefix(hostname);
  let cookiesDeleted = false;

  for (const name of getCookieNames(cookieString)) {
    const preserveCookie =
      isPlatformCookie(name) ||
      (isExternalEnvironment && environmentPrefix === null
        ? isEssentialUnprefixedCookie(name)
        : environmentPrefix
        ? name.startsWith(`${environmentPrefix}_`)
        : isEssentialUnprefixedCookie(name));

    if (!preserveCookie) {
      deleteCookieForAllScopes(name, hostname, deleteCookie);
      cookiesDeleted = true;
    }
  }

  return {
    cookiesDeleted,
    shouldReload: cookiesDeleted && !isExternalEnvironment,
  };
}

export function cleanupCurrentBrowserCookies(): BrowserCookieCleanupResult {
  return cleanupBrowserCookies({
    cookieString: document.cookie,
    hostname: window.location.hostname,
    deleteCookie: (value) => {
      document.cookie = value;
    },
  });
}
